import { useMutation, useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { DAIForm } from 'src/DAIComponents/Form/DAIForm'
import { DAIRequest } from 'src/utils/DAIRequest'
import {
  OrganizationSettingsForm,
  organizationSettingsFormSchema,
} from '../SettingsTypes'
import { zodResolver } from '@hookform/resolvers/zod'
import DAIButton from 'src/DAIComponents/Input/Button/DAIButton'
import { useNotifications } from 'src/contexts/notificationContext'
import { useEffect } from 'react'

export function UpdateOrganizationSection() {
  const { addNotification } = useNotifications()
  const { data: timezones } = useQuery<string[]>({
    queryKey: ['timezones'],
    queryFn: async () =>
      (await DAIRequest('GET', '/api/v1/authentication/getTimezones')).data,
  })

  const { data: orgTimezone, refetch } = useQuery<string>({
    queryKey: ['orgTimezone'],
    queryFn: async () =>
      (await DAIRequest('GET', '/api/v1/organization/timezone')).data,
  })

  const { mutateAsync: onUpdate } = useMutation({
    mutationFn: async (data: OrganizationSettingsForm) =>
      (
        await DAIRequest('POST', '/api/v1/organization/timezone', {
          body: data,
        })
      ).data,
    onSuccess: () => addNotification('success', 'Sucessfully updated'),
    onError: () => addNotification('error', 'Failed to update'),
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<OrganizationSettingsForm>({
    resolver: zodResolver(organizationSettingsFormSchema),
    defaultValues: { timezone: orgTimezone },
  })

  useEffect(() => {
    if (orgTimezone) {
      reset({ timezone: orgTimezone })
    }
  }, [orgTimezone, reset])

  const handleUpdate = async (data: OrganizationSettingsForm) => {
    await onUpdate(data)
    await refetch()
  }

  return (
    <>
      <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 pt-2 pb-16 sm:px-6 md:grid-cols-3 lg:px-8">
        <div>
          <h2 className="text-base font-semibold leading-7 text-gray-900">
            Organization Level Settings
          </h2>
          <p className="mt-1 text-sm leading-6 text-gray-400">
            Timezone, event types for appointments, etc.
          </p>
        </div>

        <DAIForm
          className="md:col-span-2"
          onSubmit={handleSubmit(async data => await handleUpdate(data))}
        >
          <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:max-w-xl sm:grid-cols-6">
            <div className="col-span-full">
              <label
                htmlFor="timezone"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Timezone
                <p className="mt-1 text-sm leading-6 text-gray-400 font-normal">
                  This will change the timezone for the entire organization. It
                  is also used to schedule emails and appointments. Thus it is
                  important to set it correctly.
                </p>
              </label>
              <div className="mt-2">
                <select
                  id="timezone"
                  className="block w-full rounded-md border-0 bg-white/5 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6 [&_*]:text-black"
                  {...register('timezone')}
                >
                  {timezones?.map((timezone, i) => {
                    return (
                      <option value={timezone} key={i}>
                        {timezone.replace('_', ' ')}
                      </option>
                    )
                  })}
                </select>
                <p className="mt-2 text-sm text-red-600">
                  {errors.timezone?.message}
                </p>
              </div>
            </div>
          </div>
          <DAIButton buttonType="submit" text="Save" className="mt-4" />
        </DAIForm>
      </div>
    </>
  )
}
